import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Check, ExternalLink, Sparkles, Zap, Trophy, Rocket, Gift, Music, Volume2, Smartphone, Monitor, Users, Timer, Headphones, VolumeX } from 'lucide-react';
import { FaTwitch, FaTiktok, FaYoutube, FaInstagram } from 'react-icons/fa';
import confetti from 'canvas-confetti';
import { playClick, playSuccess, playWhoosh, playCelebration, playStart, playLevelUp, playCoin } from '../utils/gameSounds';

/**
 * GoLiveGuideTour - Gamified step-by-step wizard for new reviewers
 * 
 * TWO PATHS:
 * 1. Quick Start (2 min) - TikTok Business + Live Mobile - RECOMMENDED
 * 2. Pro Setup - Full 2-device audio setup for advanced users
 * 
 * Features:
 * - 🎮 Game-like sound effects
 * - Progress bar with animated steps
 * - Celebration animations
 * - Saves progress to localStorage
 */

const STORAGE_KEY = 'mcm_go_live_tour_progress';
const COMPLETED_KEY = 'mcm_go_live_tour_completed';
const SOUND_PREF_KEY = 'mcm_tour_sounds_enabled';

const TWO_DEVICE_DIAGRAM_URL = 'https://static.prod-images.emergentagent.com/jobs/cb79f670-214b-4957-af67-c4eb9237b946/images/0b770061057eac9964bc142488cc8df8149868048989b34dac59315d170eb0c0.png';

const GoLiveGuideTour = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPath, setSelectedPath] = useState(null); // 'quick' or 'pro'
  const [isAnimating, setIsAnimating] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem(SOUND_PREF_KEY);
    return saved !== 'false'; // Default to enabled
  });

  // Save sound preference
  useEffect(() => {
    localStorage.setItem(SOUND_PREF_KEY, soundEnabled.toString());
  }, [soundEnabled]);

  // Sound helper
  const playSound = (soundFn) => {
    if (soundEnabled) {
      soundFn();
    }
  };

  // Load saved progress on mount OR reset if no progress
  useEffect(() => {
    if (isOpen) {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setCurrentStep(parsed.step || 0);
          setSelectedPath(parsed.path || null);
        } catch (e) {
          console.error('Failed to parse tour progress:', e);
          // Reset on parse error
          setCurrentStep(0);
          setSelectedPath(null);
        }
      } else {
        // No saved progress - reset to beginning
        setCurrentStep(0);
        setSelectedPath(null);
      }
      // Play start sound
      setTimeout(() => playSound(playStart), 300);
    }
  }, [isOpen]);

  // Save progress on change
  useEffect(() => {
    if (isOpen && currentStep > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        step: currentStep,
        path: selectedPath
      }));
    }
  }, [currentStep, selectedPath, isOpen]);

  const handleNext = () => {
    playSound(playWhoosh);
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(prev => prev + 1);
      setIsAnimating(false);
      playSound(playSuccess);
    }, 200);
  };

  const handleBack = () => {
    playSound(playClick);
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(prev => Math.max(prev - 1, 0));
      setIsAnimating(false);
    }, 200);
  };

  const handleSelectPath = (path) => {
    playSound(playLevelUp);
    setSelectedPath(path);
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(1);
      setIsAnimating(false);
    }, 300);
  };

  const handleComplete = () => {
    // Epic celebration!
    playSound(playCelebration);
    
    confetti({
      particleCount: 150,
      spread: 80,
      origin: { y: 0.6 },
      colors: ['#F2D857', '#FFD700', '#FFA500', '#FFFFFF', '#00FF00']
    });
    
    // Second burst
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#F2D857', '#FFD700']
      });
      confetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#F2D857', '#FFD700']
      });
    }, 250);
    
    localStorage.setItem(COMPLETED_KEY, 'true');
    localStorage.removeItem(STORAGE_KEY);
    
    setTimeout(() => {
      onComplete?.();
      onClose();
    }, 2000);
  };

  if (!isOpen) return null;

  // Dynamic step count based on path
  const getStepCount = () => {
    if (!selectedPath) return 1;
    return selectedPath === 'quick' ? 4 : 6;
  };

  // Progress dots
  const ProgressBar = () => {
    const totalSteps = getStepCount();
    return (
      <div className="flex items-center justify-center gap-2 mb-6">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={`transition-all duration-300 rounded-full ${
              i === currentStep 
                ? 'w-8 h-3 bg-[#F2D857] animate-pulse' 
                : i < currentStep 
                  ? 'w-3 h-3 bg-[#F2D857]/60' 
                  : 'w-3 h-3 bg-gray-600'
            }`}
          />
        ))}
      </div>
    );
  };

  // Sound toggle button
  const SoundToggle = () => (
    <button
      onClick={() => {
        setSoundEnabled(!soundEnabled);
        if (!soundEnabled) playClick();
      }}
      className="absolute top-4 left-4 z-50 w-8 h-8 flex items-center justify-center text-white/50 hover:text-white transition rounded-full hover:bg-white/10"
      title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
    >
      {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
    </button>
  );

  // ============================================
  // STEP 0: PATH SELECTION (Welcome + Choose Path)
  // ============================================
  const PathSelectionStep = () => (
    <div className="text-center space-y-5">
      <div className="relative inline-block">
        <div className="text-6xl animate-bounce">🎤</div>
        <Sparkles className="absolute -top-2 -right-2 w-8 h-8 text-[#F2D857] animate-pulse" />
      </div>
      
      <h2 className="text-2xl font-black text-white">
        Let's Get You <span className="text-[#F2D857]">LIVE!</span>
      </h2>
      
      <p className="text-gray-400 text-sm">
        Choose your path to streaming success
      </p>

      {/* QUICK START - Recommended */}
      <button
        onClick={() => handleSelectPath('quick')}
        className="w-full p-4 rounded-2xl border-2 border-[#F2D857] bg-[#F2D857]/10 text-left transition-all hover:scale-[1.02] hover:bg-[#F2D857]/20 active:scale-[0.98] relative overflow-hidden group"
      >
        <div className="absolute top-0 right-0 bg-[#F2D857] text-black text-xs font-bold px-3 py-1 rounded-bl-xl">
          ⚡ FASTEST
        </div>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-[#F2D857] flex items-center justify-center">
            <Timer className="w-7 h-7 text-black" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-white text-lg flex items-center gap-2">
              Quick Start
              <span className="text-[#F2D857] text-sm font-normal">2 min</span>
            </h3>
            <p className="text-gray-400 text-sm">TikTok + Live Mobile</p>
            <p className="text-green-400 text-xs mt-1">📱 Just need phone + headphones</p>
          </div>
          <ChevronRight className="w-6 h-6 text-[#F2D857] group-hover:translate-x-1 transition-transform" />
        </div>
      </button>

      {/* PRO SETUP */}
      <button
        onClick={() => handleSelectPath('pro')}
        className="w-full p-4 rounded-2xl border-2 border-gray-700 bg-[#111] text-left transition-all hover:scale-[1.01] hover:border-gray-500 active:scale-[0.98] group"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
            <Trophy className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-white text-lg flex items-center gap-2">
              Pro Setup
              <span className="text-gray-400 text-sm font-normal">10-15 min</span>
            </h3>
            <p className="text-gray-400 text-sm">Any platform + Full audio routing</p>
            <p className="text-blue-400 text-xs mt-1">🎧 2 devices + audio interface</p>
          </div>
          <ChevronRight className="w-6 h-6 text-gray-500 group-hover:translate-x-1 transition-transform" />
        </div>
      </button>

      <p className="text-gray-500 text-xs">
        💡 Most reviewers start with Quick Start, then level up later!
      </p>
    </div>
  );

  // ============================================
  // QUICK START PATH STEPS
  // ============================================
  
  // Quick Step 1: TikTok Business Setup
  const QuickStep1 = () => (
    <div className="space-y-3">
      <div className="text-center mb-2">
        <div className="inline-flex items-center gap-2 bg-[#F2D857]/20 px-3 py-1.5 rounded-full mb-2">
          <Timer className="w-3 h-3 text-[#F2D857]" />
          <span className="text-[#F2D857] font-bold text-xs">QUICK START</span>
        </div>
        <h2 className="text-xl font-bold text-white">
          Get <span className="text-[#F2D857]">TikTok Ready</span>
        </h2>
        <p className="text-gray-400 text-xs mt-1">Switch to Business Account (free!)</p>
      </div>

      {/* TikTok Card */}
      <div className="bg-[#111] rounded-xl p-3 border border-gray-800">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center border border-gray-700">
            <FaTiktok className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-white text-sm">TikTok Business Account</h3>
            <p className="text-green-400 text-xs">No followers required! 🎉</p>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 p-2 bg-black/50 rounded-lg">
            <div className="w-5 h-5 rounded-full bg-[#F2D857] text-black font-bold flex items-center justify-center flex-shrink-0 text-[10px]">1</div>
            <p className="text-gray-300 text-xs">Open TikTok → <strong className="text-white">Profile</strong></p>
          </div>
          <div className="flex items-center gap-2 p-2 bg-black/50 rounded-lg">
            <div className="w-5 h-5 rounded-full bg-[#F2D857] text-black font-bold flex items-center justify-center flex-shrink-0 text-[10px]">2</div>
            <p className="text-gray-300 text-xs">Tap <strong className="text-white">☰ Menu → Settings</strong></p>
          </div>
          <div className="flex items-center gap-2 p-2 bg-black/50 rounded-lg">
            <div className="w-5 h-5 rounded-full bg-[#F2D857] text-black font-bold flex items-center justify-center flex-shrink-0 text-[10px]">3</div>
            <p className="text-gray-300 text-xs"><strong className="text-white">Account → Switch to Business</strong></p>
          </div>
          <div className="flex items-center gap-2 p-2 bg-black/50 rounded-lg">
            <div className="w-5 h-5 rounded-full bg-green-500 text-white font-bold flex items-center justify-center flex-shrink-0 text-[10px]">✓</div>
            <p className="text-gray-300 text-xs">Select <strong className="text-white">"Music"</strong> category</p>
          </div>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2">
        <p className="text-blue-400 text-xs text-center">
          ⏱️ Takes <strong>30 seconds</strong> - you only do it once!
        </p>
      </div>

      {/* Navigation */}
      <div className="flex gap-3 pt-1">
        <button
          onClick={handleBack}
          className="flex-1 py-2.5 rounded-full border border-gray-600 text-gray-300 hover:bg-gray-800 transition flex items-center justify-center gap-2 text-sm"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={handleNext}
          className="flex-1 py-2.5 rounded-full font-bold bg-[#F2D857] text-black hover:scale-[1.02] transition flex items-center justify-center gap-2 text-sm"
        >
          Done! Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // Quick Step 2: Gear Check
  const QuickStep2 = () => (
    <div className="space-y-3">
      <div className="text-center mb-2">
        <div className="inline-flex items-center gap-2 bg-[#F2D857]/20 px-3 py-1.5 rounded-full mb-2">
          <Timer className="w-3 h-3 text-[#F2D857]" />
          <span className="text-[#F2D857] font-bold text-xs">QUICK START</span>
        </div>
        <h2 className="text-xl font-bold text-white">
          <span className="text-[#F2D857]">Gear</span> Check
        </h2>
        <p className="text-gray-400 text-xs mt-1">You probably already have everything!</p>
      </div>

      {/* Gear checklist */}
      <div className="space-y-2">
        <div className="flex items-center gap-3 p-3 bg-[#111] rounded-xl border border-green-500/30">
          <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
            <Smartphone className="w-5 h-5 text-green-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-white text-sm">Smartphone</h3>
            <p className="text-gray-400 text-xs">iPhone or Android</p>
          </div>
          <Check className="w-5 h-5 text-green-400" />
        </div>

        <div className="flex items-center gap-3 p-3 bg-[#111] rounded-xl border border-green-500/30">
          <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
            <Headphones className="w-5 h-5 text-green-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-white text-sm">Headphones with Mic</h3>
            <p className="text-gray-400 text-xs">Wired earbuds work great!</p>
          </div>
          <Check className="w-5 h-5 text-green-400" />
        </div>
      </div>

      <div className="bg-[#F2D857]/10 border border-[#F2D857]/30 rounded-lg p-3 text-center">
        <p className="text-[#F2D857] font-bold">That's it! 🎉</p>
        <p className="text-gray-400 text-xs mt-0.5">No extra equipment needed</p>
      </div>

      {/* Pro tip */}
      <div className="bg-[#111] rounded-lg p-2 border border-gray-800">
        <p className="text-gray-400 text-xs text-center">
          💡 <span className="text-white">Pro tip:</span> Wired headphones work better than Bluetooth
        </p>
      </div>

      {/* Navigation */}
      <div className="flex gap-3 pt-1">
        <button
          onClick={handleBack}
          className="flex-1 py-2.5 rounded-full border border-gray-600 text-gray-300 hover:bg-gray-800 transition flex items-center justify-center gap-2 text-sm"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={handleNext}
          className="flex-1 py-2.5 rounded-full font-bold bg-[#F2D857] text-black hover:scale-[1.02] transition flex items-center justify-center gap-2 text-sm"
        >
          Got it! <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // Quick Step 3: Go Live Steps
  const QuickStep3 = () => (
    <div className="space-y-3">
      <div className="text-center mb-2">
        <div className="inline-flex items-center gap-2 bg-[#F2D857]/20 px-3 py-1.5 rounded-full mb-2">
          <Zap className="w-3 h-3 text-[#F2D857]" />
          <span className="text-[#F2D857] font-bold text-xs">FINAL STEP</span>
        </div>
        <h2 className="text-xl font-bold text-white">
          <span className="text-[#F2D857]">Go Live</span> in 60 Seconds
        </h2>
      </div>

      {/* Step-by-step */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 p-2 bg-[#111] rounded-lg border border-gray-800">
          <div className="w-6 h-6 rounded-full bg-[#F2D857] text-black font-bold flex items-center justify-center flex-shrink-0 text-xs">1</div>
          <p className="text-white text-sm">Open MCM session → <strong>"Live Mobile"</strong></p>
        </div>

        <div className="flex items-center gap-2 p-2 bg-[#111] rounded-lg border border-gray-800">
          <div className="w-6 h-6 rounded-full bg-[#F2D857] text-black font-bold flex items-center justify-center flex-shrink-0 text-xs">2</div>
          <p className="text-white text-sm">Phone: <span className="text-gray-400">🔕 DND • 📱 Lock • ☀️ On</span></p>
        </div>

        <div className="flex items-center gap-2 p-2 bg-[#111] rounded-lg border border-gray-800">
          <div className="w-6 h-6 rounded-full bg-[#F2D857] text-black font-bold flex items-center justify-center flex-shrink-0 text-xs">3</div>
          <p className="text-white text-sm">TikTok → Live → Gaming → <strong>Screen Share</strong></p>
        </div>

        <div className="flex items-center gap-2 p-2 bg-[#111] rounded-lg border border-gray-800">
          <div className="w-6 h-6 rounded-full bg-[#F2D857] text-black font-bold flex items-center justify-center flex-shrink-0 text-xs">4</div>
          <p className="text-white text-sm">Switch back to <strong>MCM tab</strong></p>
        </div>

        <div className="flex items-center gap-2 p-2.5 bg-green-500/10 rounded-lg border border-green-500/30">
          <div className="w-6 h-6 rounded-full bg-green-500 text-white font-bold flex items-center justify-center flex-shrink-0 text-xs">✓</div>
          <p className="text-green-400 font-bold text-sm">You're LIVE! 🎉</p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex gap-3 pt-1">
        <button
          onClick={handleBack}
          className="flex-1 py-2.5 rounded-full border border-gray-600 text-gray-300 hover:bg-gray-800 transition flex items-center justify-center gap-2 text-sm"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={handleNext}
          className="flex-1 py-2.5 rounded-full font-bold bg-[#F2D857] text-black hover:scale-[1.02] transition flex items-center justify-center gap-2 text-sm"
        >
          I'm Ready! <Rocket className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // Quick Step 4: Celebration
  const QuickCompletion = () => (
    <div className="text-center space-y-5">
      <div className="relative inline-block">
        <div className="text-7xl animate-bounce">🎉</div>
        <Sparkles className="absolute -top-2 -right-4 w-8 h-8 text-[#F2D857] animate-pulse" />
        <Sparkles className="absolute -bottom-2 -left-4 w-6 h-6 text-green-400 animate-pulse delay-100" />
      </div>
      
      <h2 className="text-3xl font-black text-white">
        You're <span className="text-[#F2D857]">READY!</span>
      </h2>
      
      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
        <p className="text-green-400 font-bold text-lg flex items-center justify-center gap-2">
          <Timer className="w-5 h-5" />
          Quick Start Complete!
        </p>
        <p className="text-gray-400 text-sm mt-1">You can go live in under 2 minutes</p>
      </div>

      <div className="bg-[#111] rounded-xl p-4 text-left space-y-2 border border-gray-800">
        <p className="text-gray-400 text-sm">Your setup:</p>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-green-400" />
          <span className="text-white">TikTok Business Account</span>
        </div>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-green-400" />
          <span className="text-white">Live Mobile + Screen Share</span>
        </div>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-green-400" />
          <span className="text-white">Phone + Headphones</span>
        </div>
      </div>

      {/* CTAs */}
      <div className="space-y-3">
        <button
          onClick={handleComplete}
          className="w-full py-4 rounded-full font-bold text-black text-lg transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
          style={{ 
            background: 'linear-gradient(180deg, #F2D857 0%, #C9A227 100%)',
            boxShadow: '0 4px 20px rgba(242, 216, 87, 0.4)'
          }}
        >
          <Music className="w-6 h-6" />
          Let's Go Live!
        </button>
        
        <button
          onClick={() => {
            playSound(playCoin);
            setSelectedPath('pro');
            setCurrentStep(1);
          }}
          className="w-full py-3 rounded-full border border-gray-600 text-gray-300 hover:bg-gray-800 transition flex items-center justify-center gap-2 text-sm"
        >
          <Trophy className="w-4 h-4" />
          Want to level up? View Pro Setup
        </button>
      </div>

      <p className="text-gray-500 text-xs">
        Now go make some noise! 🎤💰
      </p>
    </div>
  );

  // ============================================
  // PRO SETUP PATH STEPS
  // ============================================

  // Pro Step 1: Platform Selection
  const ProStep1 = () => (
    <div className="space-y-3">
      <div className="text-center mb-2">
        <div className="inline-flex items-center gap-2 bg-purple-500/20 px-3 py-1.5 rounded-full mb-2">
          <Trophy className="w-3 h-3 text-purple-400" />
          <span className="text-purple-400 font-bold text-xs">PRO SETUP</span>
        </div>
        <h2 className="text-xl font-bold text-white">
          Choose Your <span className="text-[#F2D857]">Platform</span>
        </h2>
      </div>

      {/* Platform options */}
      <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
        {/* Twitch - Recommended */}
        <button
          onClick={() => { playSound(playClick); handleNext(); }}
          className="w-full p-2.5 rounded-xl border-2 border-[#9146FF] bg-[#9146FF]/10 text-left transition-all hover:scale-[1.01] relative"
        >
          <div className="absolute top-1.5 right-1.5 bg-[#F2D857] text-black text-[9px] font-bold px-1.5 py-0.5 rounded-full">
            ⭐ BEST
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#9146FF] flex items-center justify-center">
              <FaTwitch className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="font-bold text-white text-sm">Twitch</span>
              <p className="text-green-400 text-[10px]">No follower requirement!</p>
            </div>
          </div>
        </button>

        {/* TikTok */}
        <button
          onClick={() => { playSound(playClick); handleNext(); }}
          className="w-full p-2.5 rounded-xl border border-gray-700 bg-[#111] text-left transition-all hover:border-gray-500"
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center border border-gray-700">
              <FaTiktok className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="font-bold text-white text-sm">TikTok</span>
              <p className="text-gray-400 text-[10px]">1K followers or Business account</p>
            </div>
          </div>
        </button>

        {/* YouTube */}
        <button
          onClick={() => { playSound(playClick); handleNext(); }}
          className="w-full p-2.5 rounded-xl border border-gray-700 bg-[#111] text-left transition-all hover:border-gray-500"
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#FF0000] flex items-center justify-center">
              <FaYoutube className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="font-bold text-white text-sm">YouTube</span>
              <p className="text-gray-400 text-[10px]">24hr verification first time</p>
            </div>
          </div>
        </button>

        {/* Instagram */}
        <button
          onClick={() => { playSound(playClick); handleNext(); }}
          className="w-full p-2.5 rounded-xl border border-gray-700 bg-[#111] text-left transition-all hover:border-gray-500"
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(45deg, #F58529, #DD2A7B, #8134AF)' }}>
              <FaInstagram className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="font-bold text-white text-sm">Instagram</span>
              <p className="text-gray-400 text-[10px]">1K followers + Pro account</p>
            </div>
          </div>
        </button>

        {/* Multi-platform */}
        <button
          onClick={() => { playSound(playClick); handleNext(); }}
          className="w-full p-2.5 rounded-xl border border-gray-700 bg-[#111] text-left transition-all hover:border-gray-500"
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#F2D857] flex items-center justify-center">
              <Users className="w-4 h-4 text-black" />
            </div>
            <div>
              <span className="font-bold text-white text-sm">Multiple Platforms</span>
              <p className="text-gray-400 text-[10px]">Restream or Streamlabs</p>
            </div>
          </div>
        </button>
      </div>

      {/* Back */}
      <button
        onClick={handleBack}
        className="w-full py-2.5 rounded-full border border-gray-600 text-gray-300 hover:bg-gray-800 transition flex items-center justify-center gap-2 text-sm"
      >
        <ChevronLeft className="w-4 h-4" /> Back to Path Selection
      </button>
    </div>
  );

  // Pro Step 2: Device Setup
  const ProStep2 = () => (
    <div className="space-y-3">
      <div className="text-center mb-2">
        <div className="inline-flex items-center gap-2 bg-purple-500/20 px-3 py-1.5 rounded-full mb-2">
          <Trophy className="w-3 h-3 text-purple-400" />
          <span className="text-purple-400 font-bold text-xs">PRO SETUP</span>
        </div>
        <h2 className="text-xl font-bold text-white">
          <span className="text-[#F2D857]">2-Device</span> Setup
        </h2>
        <p className="text-gray-400 text-xs mt-1">This is how your audience hears the music!</p>
      </div>

      {/* Diagram */}
      <div className="rounded-xl overflow-hidden border border-[#F2D857]/30">
        <img 
          src={TWO_DEVICE_DIAGRAM_URL} 
          alt="Two device setup diagram" 
          className="w-full h-auto"
        />
      </div>

      {/* Simple explanation */}
      <div className="bg-[#111] rounded-lg p-3 border border-gray-800">
        <div className="space-y-1.5 text-xs">
          <p className="text-gray-300">
            <span className="text-[#F2D857] font-bold">Device 1:</span> Goes LIVE (streaming phone/laptop)
          </p>
          <p className="text-gray-300">
            <span className="text-[#F2D857] font-bold">Device 2:</span> Runs MCM (plays music)
          </p>
          <p className="text-gray-300">
            <span className="text-[#F2D857] font-bold">Audio Interface:</span> Combines voice + music
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex gap-3 pt-1">
        <button
          onClick={handleBack}
          className="flex-1 py-2.5 rounded-full border border-gray-600 text-gray-300 hover:bg-gray-800 transition flex items-center justify-center gap-2 text-sm"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={handleNext}
          className="flex-1 py-2.5 rounded-full font-bold bg-[#F2D857] text-black hover:scale-[1.02] transition flex items-center justify-center gap-2 text-sm"
        >
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // Pro Step 3: Second Device Options
  const ProStep3 = () => (
    <div className="space-y-3">
      <div className="text-center mb-2">
        <h2 className="text-xl font-bold text-white">
          Your <span className="text-[#F2D857]">Second Device</span>
        </h2>
        <p className="text-gray-400 text-xs mt-1">For running Mic Check Millionaire</p>
      </div>

      <div className="space-y-2">
        <div className="p-3 bg-[#111] rounded-xl border border-gray-800">
          <h3 className="font-bold text-white text-sm mb-1.5">Options:</h3>
          <ul className="space-y-1 text-xs text-gray-300">
            <li className="flex items-start gap-2">
              <span className="text-[#F2D857]">•</span>
              Spare phone or tablet
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#F2D857]">•</span>
              Family member's device (just borrow it!)
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#F2D857]">•</span>
              Laptop while streaming from phone
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#F2D857]">•</span>
              Old phone on WiFi (doesn't need service)
            </li>
          </ul>
        </div>

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2">
          <p className="text-blue-400 text-xs text-center">
            💡 Starting a business sometimes means borrowing mom's iPad!
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex gap-3 pt-1">
        <button
          onClick={handleBack}
          className="flex-1 py-2.5 rounded-full border border-gray-600 text-gray-300 hover:bg-gray-800 transition flex items-center justify-center gap-2 text-sm"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={handleNext}
          className="flex-1 py-2.5 rounded-full font-bold bg-[#F2D857] text-black hover:scale-[1.02] transition flex items-center justify-center gap-2 text-sm"
        >
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // Pro Step 4: Equipment
  const ProStep4 = () => (
    <div className="space-y-3">
      <div className="text-center mb-2">
        <h2 className="text-xl font-bold text-white">
          <span className="text-[#F2D857]">Equipment</span> Guide
        </h2>
        <p className="text-gray-400 text-xs mt-1">Your personality is the star - gear is bonus!</p>
      </div>

      <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1">
        {/* Gold - Key for audio */}
        <div className="p-2.5 bg-[#111] rounded-xl border border-[#F2D857]/50 relative">
          <div className="absolute top-1.5 right-1.5 bg-[#F2D857] text-black text-[9px] font-bold px-1.5 py-0.5 rounded-full">
            FOR MUSIC
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xl">🥇</span>
            <div className="flex-1">
              <h4 className="font-bold text-white text-sm">Audio Interface</h4>
              <p className="text-gray-400 text-[10px]">So audience hears the music!</p>
            </div>
            <span className="text-[#F2D857] font-bold text-sm">~$60</span>
          </div>
          <a href="https://amzn.to/43sFuiF" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#F2D857] text-[10px] mt-1 hover:underline">
            Maono Bundle <ExternalLink className="w-2.5 h-2.5" />
          </a>
        </div>

        {/* Silver */}
        <div className="p-2.5 bg-[#111] rounded-xl border border-gray-700">
          <div className="flex items-center gap-2">
            <span className="text-xl">🥈</span>
            <div className="flex-1">
              <h4 className="font-bold text-white text-sm">Tripod + Ring Light</h4>
              <p className="text-gray-400 text-[10px]">Look 10x more professional</p>
            </div>
            <span className="text-gray-300 font-bold text-sm">$50-100</span>
          </div>
        </div>

        {/* Diamond */}
        <div className="p-2.5 bg-[#111] rounded-xl border border-gray-700">
          <div className="flex items-center gap-2">
            <span className="text-xl">💎</span>
            <div className="flex-1">
              <h4 className="font-bold text-white text-sm">Full Pro Setup</h4>
              <p className="text-gray-400 text-[10px]">SM7B mic, Scarlett interface, etc.</p>
            </div>
            <span className="text-gray-300 font-bold text-sm">$300+</span>
          </div>
          <p className="text-gray-500 text-[10px] mt-1">📚 Full details in the Blueprint e-book</p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex gap-3 pt-1">
        <button
          onClick={handleBack}
          className="flex-1 py-2.5 rounded-full border border-gray-600 text-gray-300 hover:bg-gray-800 transition flex items-center justify-center gap-2 text-sm"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={handleNext}
          className="flex-1 py-2.5 rounded-full font-bold bg-[#F2D857] text-black hover:scale-[1.02] transition flex items-center justify-center gap-2 text-sm"
        >
          Almost done! <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // Pro Step 5: Completion
  const ProCompletion = () => (
    <div className="text-center space-y-4">
      <div className="relative inline-block">
        <div className="text-5xl animate-bounce">🏆</div>
        <Sparkles className="absolute -top-1 -right-3 w-6 h-6 text-purple-400 animate-pulse" />
      </div>
      
      <h2 className="text-2xl font-black text-white">
        <span className="text-purple-400">Pro</span> Status Unlocked!
      </h2>
      
      <p className="text-gray-300 text-sm">
        You have all the knowledge for a professional setup!
      </p>

      {/* CTAs */}
      <div className="space-y-2">
        <button
          onClick={handleComplete}
          className="w-full py-3 rounded-full font-bold text-black text-base transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
          style={{ 
            background: 'linear-gradient(180deg, #F2D857 0%, #C9A227 100%)',
            boxShadow: '0 4px 20px rgba(242, 216, 87, 0.4)'
          }}
        >
          <Music className="w-5 h-5" />
          Start Reviewing!
        </button>
        
        <a
          href="https://miccheckmillionaire.gumroad.com/l/zzlxg"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full py-2.5 rounded-full border border-[#F2D857] text-[#F2D857] font-bold transition hover:bg-[#F2D857]/10 flex items-center justify-center gap-2 text-sm"
        >
          <Gift className="w-4 h-4" />
          Get the Full Blueprint
        </a>
      </div>

      <p className="text-gray-500 text-xs">
        Now go make some noise! 🎤💰
      </p>
    </div>
  );

  // Render current step based on path
  const renderStep = () => {
    if (!selectedPath) {
      return <PathSelectionStep />;
    }

    if (selectedPath === 'quick') {
      switch (currentStep) {
        case 1: return <QuickStep1 />;
        case 2: return <QuickStep2 />;
        case 3: return <QuickStep3 />;
        case 4: return <QuickCompletion />;
        default: return <PathSelectionStep />;
      }
    }

    if (selectedPath === 'pro') {
      switch (currentStep) {
        case 1: return <ProStep1 />;
        case 2: return <ProStep2 />;
        case 3: return <ProStep3 />;
        case 4: return <ProStep4 />;
        case 5: return <ProCompletion />;
        default: return <PathSelectionStep />;
      }
    }

    return <PathSelectionStep />;
  };

  return (
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/95 backdrop-blur-sm p-3 overflow-y-auto"
      data-testid="go-live-guide-tour"
    >
      <div className={`relative w-full max-w-md bg-[#0a0a0a] rounded-2xl shadow-2xl border border-[#333] overflow-hidden transition-all duration-300 max-h-[90vh] overflow-y-auto ${isAnimating ? 'opacity-50 scale-95' : 'opacity-100 scale-100'}`}>
        {/* Sound Toggle */}
        <SoundToggle />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-50 w-7 h-7 flex items-center justify-center text-white/50 hover:text-white transition rounded-full hover:bg-white/10"
          data-testid="go-live-tour-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Content */}
        <div className="p-4 pt-10">
          {selectedPath && currentStep > 0 && currentStep < getStepCount() && <ProgressBar />}
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default GoLiveGuideTour;
